import { booleanAttribute, ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { SelectionItem } from '../../../core/component/select/select.component';
import { getMonthName } from '../../../core/helper/month';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService, SWEDISH_CULTURE } from '../../../core/service/language.service';
import { LettingAdvertisment } from '../../model/letting-advertisment';

@Component({
  selector: 'letting-advertisment-boplats-sverige',
  templateUrl: 'letting-advertisment-boplats-sverige.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingAdvertismentBoplatsSverigeComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) letting: LettingAdvertisment;
  @Input({ transform: booleanAttribute }) readonly: boolean = false;

  public form: UntypedFormGroup;

  protected monthSelection: SelectionItem[] = Array(12)
    .fill(undefined)
    .map((_, i) => new SelectionItem(getMonthName(i + 1), i + 1));

  private destroy = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formFactory: FormControlExFactory,
    private readonly languageService: LanguageService
  ) {}

  ngOnInit(): void {
    const objectDescription =
      this.letting.objectDescription?.find(x => x.cultureName === SWEDISH_CULTURE)?.translationText ||
      this.letting.objectDescription?.find(x => x.translationText)?.translationText;

    this.form = this.formBuilder.group({
      vacancyFrom: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.VACANT_FROM', this.letting.vacancyFrom, {
        required: true,
        isReadonly: this.readonly,
        validator: this.emptyLocalDateValidator,
      }),
      vacancyTo: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.VACANT_TO', this.letting.vacancyTo, {
        isReadonly: this.readonly,
      }),
      publishFrom: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.PUBLISH_FROM', LocalDate.fromDate(this.letting.publishFrom), {
        required: true,
        isReadonly: this.readonly,
        validator: this.emptyLocalDateValidator,
      }),
      publishTo: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.PUBLISH_TO', LocalDate.fromDate(this.letting.publishTo), {
        required: true,
        isReadonly: this.readonly,
        validator: this.emptyLocalDateValidator,
      }),
      monthlyRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.MONTHLY_RENT', this.letting.monthlyRent, {
        required: true,
        isReadonly: this.readonly,
      }),
      yearlyRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.YEARLY_RENT', this.letting.yearlyRent, {
        required: true,
        isReadonly: this.readonly,
      }),
      monthsWithRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.MONTHS_WITH_RENT', this.letting.monthsWithRent, {
        isReadonly: this.readonly,
      }),
      boplatsSverigeDescriptionURL: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.DESCRIPTION_URL',
        this.letting.boplatsSverigeDescriptionURL,
        {
          required: true,
          isReadonly: this.readonly,
        }
      ),
      objectDescription: this.formFactory.createHidden(this.letting.objectDescription),
      boplatsSverigeObjectDescription: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.OBJECT_DESCRIPTION', objectDescription, {
        required: true,
        isReadonly: this.readonly,
        validator: this.descriptionValidator,
      }),
      uniqueObjectDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DONT_UPDATE_DESCRIPTION',
        { value: this.letting.uniqueObjectDescription, disabled: this.readonly }, // mat checkboxes are anyoing, let us do bad code
        { isReadonly: this.readonly, helpText: 'LETTING.ADVERTISMENT.HELP_TEXT.DONT_UPDATE_DESCRIPTION_OBJECT' }
      ),
      boplatsSverigeReadiness: this.formFactory.create(
        'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.READINESS',
        null,
        this.publishReadinessValidator
      ),
    });

    this.form.controls.boplatsSverigeObjectDescription.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: value => {
        this.form.controls.objectDescription.setValue([
          ...(this.letting.objectDescription ?? []).filter(x => x.cultureName !== SWEDISH_CULTURE),
          { cultureName: SWEDISH_CULTURE, translationText: value },
        ]);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly && this.form) {
      this.applyReadonly(this.form);
    }
  }

  private emptyLocalDateValidator: ValidatorFn = (control: AbstractControl<LocalDate>): { [key: string]: any } | null =>
    control.value?.year !== null && control.value?.month !== null && control.value?.day !== null ? null : { required: 'required' };

  public get missingPublishFields(): string[] {
    return [
      this.letting.boplatsSverigeLocation ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.LOCATION',
      this.letting.boplatsSverigeMunicipality ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.MUNICIPALITY',
      this.letting.boplatsSverigeLivingArea > 0 ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.LIVING_AREA',
      this.letting.boplatsSverigeRooms > 0 ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.ROOMS',
      this.letting.boplatsSverigeStreetAddress ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.STREET_ADDRESS',
    ].filter((x): x is string => !!x);
  }

  private publishReadinessValidator: ValidatorFn = (): ValidationErrors | null =>
    this.missingPublishFields.length === 0
      ? null
      : {
          boplatsSverigeReadiness: {
            message: 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.ERRORS.MISSING_OBJECT_FIELDS',
          },
        };

  private applyReadonly(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.keys(control.controls).forEach(key => this.applyReadonly(control.controls[key]));
      return;
    }

    if (control instanceof FormControlEx) {
      control.isReadonly = this.readonly;
    }

    control.disable();
    control.updateValueAndValidity({ emitEvent: false });
  }

  private descriptionValidator: ValidatorFn = (c: AbstractControl) => {
    if (!c.value) {
      return null;
    }

    if (c.value.length > 3000) {
      return {
        error: {
          message: 'ERROR.FORMAT_PRM',
          params: {
            0: this.languageService
              .getText('ERROR.MAX_LENGTH')
              .replace('{{requiredLength}}', '3000')
              .replace('{{actualLength}}', c.value.length.toString()),
          },
        },
      };
    }

    return null;
  };
}
